/**
 * GameState - Save schema + (de)serialization helper
 *
 * NOTE: This is NOT a central game-state owner (that design was abandoned — see
 * notes/architecture.md). It survives as the typed shape of a save file: main.js's
 * captureGameState()/applyGameState() build and read this schema. Bump VERSION whenever
 * the shape changes; older saves then fail the version check and are treated as "no save".
 */

export class GameState {
  constructor() {
    // Surface resume position (we always resume on the surface; see applyGameState).
    this.player = null;       // { x, y }
    // Tools/inventory: which items are owned + equip state + current selection.
    this.inventory = {};      // { axe, pickaxe, sword, raygun, builder, hat, jetback, toolbox,
                              //   hatEquipped, jetbackEquipped, jetbackActive, selectedWeapon }
    // Collected currencies.
    this.currency = {};       // { chopped, diamond, feather, silk }
    // Progression flags that change the world so it doesn't reset on resume.
    this.flags = {};          // { shrineActivated, barrierVinesRemoved }
  }

  /**
   * Serialize to a plain, JSON-safe save object.
   * @returns {Object}
   */
  serialize() {
    return {
      version: GameState.VERSION,
      timestamp: Date.now(),
      player: this.player ? { x: this.player.x, y: this.player.y } : null,
      inventory: { ...this.inventory },
      currency: { ...this.currency },
      flags: { ...this.flags },
    };
  }

  /**
   * Rebuild a GameState from a saved object. Returns an empty state (player === null)
   * if the data is missing or from an incompatible version — callers should treat that
   * as "no usable save" and start fresh.
   * @param {Object} data
   * @returns {GameState}
   */
  static deserialize(data) {
    const state = new GameState();
    if (!data || data.version !== GameState.VERSION) {
      if (data && data.version) {
        console.warn(`Save version ${data.version} incompatible (need ${GameState.VERSION}); ignoring.`);
      }
      return state;
    }
    if (data.player) state.player = { x: data.player.x, y: data.player.y };
    if (data.inventory) state.inventory = { ...data.inventory };
    if (data.currency) state.currency = { ...data.currency };
    if (data.flags) state.flags = { ...data.flags };
    return state;
  }
}

// Save-schema version. Bump on any shape change to invalidate old saves.
GameState.VERSION = '2.0.0';
